import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useNotify } from '../contexts/NotifyContext';
import {
  BELL_EVENTS_MAX, NEW_EVENTS_POLL_MS, NOTIFICATIONS_PATH, TEACHER_BROWSER_OPT_IN_KEY,
} from '../constants/inbox';
import useBrowserNotifications from '../features/inbox/useBrowserNotifications';
import { notificationMessage } from '../features/inbox/inboxModel';
import useSharedPolling from './useSharedPolling';
import { canNotify } from '../features/inbox/browserNotifications';
import { INBOX_COPY as COPY } from '../constants/inbox_messages.fr';

const HTTP_CONFLICT = 409;
const EMPTY_SUMMARY = Object.freeze({ p1: 0, p2: 0, unread: 0 });
const NO_ITEMS = Object.freeze([]);

const InboxContext = createContext(null);

/**
 * NOTIF1 T13: one source for the badge, the bell, /notifications and À traiter.
 * Refetch = mount, tab visible again, every NEW_EVENTS_POLL_MS while the tab is open (N31),
 * and after every mutation (`revision` bumps so feeds reload).
 */
export function InboxProvider({ enabled, teacherId, children }) {
  const { toast } = useNotify();
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [actions, setActions] = useState({ items: NO_ITEMS, total: 0 });
  const [events, setEvents] = useState(NO_ITEMS);
  const [loaded, setLoaded] = useState(false);
  const [fetched, setFetched] = useState(false); // ≥ 1 successful fetch: baseline for browser pop-ups
  const [revision, setRevision] = useState(0);

  const refresh = useSharedPolling({
    scope: `teacher:${teacherId}`,
    enabled,
    interval: NEW_EVENTS_POLL_MS,
    allowHidden: () => canNotify(TEACHER_BROWSER_OPT_IN_KEY),
    load: async () => {
      const [snapshot, feed] = await Promise.all([
        api.getInboxSnapshot(), api.getNotifications({ limit: BELL_EVENTS_MAX }),
      ]);
      return { ...snapshot, feed };
    },
    onData: ({ summary: nextSummary, actions: nextActions, feed }) => {
      setSummary(nextSummary || EMPTY_SUMMARY);
      setActions(nextActions || { items: NO_ITEMS, total: 0 });
      setEvents(feed?.items || NO_ITEMS);
      setFetched(true);
      setLoaded(true);
    },
  });

  // N31: new rows with `browser` → OS notification; click → this tab + /notifications.
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  useBrowserNotifications({
    items: fetched ? events : null,
    storageKey: TEACHER_BROWSER_OPT_IN_KEY,
    toMessage: (item) => notificationMessage(item, 'teacher'),
    onOpen: () => navigateRef.current(NOTIFICATIONS_PATH),
  });

  const afterMutation = useCallback(async () => {
    await refresh().catch(() => {});
    setRevision((value) => value + 1);
  }, [refresh]);

  /** Returns the decided request, or null (409 → message + refetch, other error → toast). */
  const decide = useCallback(async (requestId, decision, version) => {
    try {
      const result = await api.decideScheduleRequest(requestId, decision, version);
      toast.success(COPY.decided[decision]);
      return result;
    } catch (err) {
      toast.error(err?.status === HTTP_CONFLICT ? COPY.conflict : (err?.message || COPY.error));
      return null;
    } finally {
      await afterMutation();
    }
  }, [toast, afterMutation]);

  const runOp = useCallback(async (call, successMessage) => {
    try {
      await call();
      toast.success(successMessage);
    } catch (err) {
      toast.error(err?.message || COPY.error);
    } finally {
      await afterMutation();
    }
  }, [toast, afterMutation]);

  const snooze = useCallback((key, days) => runOp(() => api.snoozeInboxAction(key, days), COPY.snoozed), [runOp]);
  const dismiss = useCallback((key) => runOp(() => api.dismissInboxAction(key), COPY.dismissed), [runOp]);

  /** `upTo` = cursor of the newest item on screen. */
  const markAllRead = useCallback(async (upTo) => {
    if (!upTo) return;
    try {
      await api.markNotificationsRead(upTo);
    } catch (err) {
      toast.error(err?.message || COPY.error);
    } finally {
      await afterMutation();
    }
  }, [toast, afterMutation]);

  const value = useMemo(() => ({
    summary, actions: actions.items, total: actions.total, events, loaded, revision,
    refresh, decide, snooze, dismiss, markAllRead,
  }), [summary, actions, events, loaded, revision, refresh, decide, snooze, dismiss, markAllRead]);

  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>;
}

export default function useInbox() {
  const value = useContext(InboxContext);
  if (!value) throw new Error('useInbox must be used inside <InboxProvider>');
  return value;
}
